import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube, Truck, CreditCard, Package, MessageCircle } from 'lucide-react';
import useSWR from 'swr';
import { publicSettings } from '../lib/api';

const Footer = () => {
  const { data: settings } = useSWR('publicSettings', publicSettings);

  const features = [
    { icon: Truck, title: 'Free Delivery', desc: 'On Prepaid Orders' },
    { icon: CreditCard, title: 'Easy Payment', desc: 'Multiple Payment options' },
    { icon: Package, title: 'Track Order', desc: 'Real time Order Tracking' },
    { icon: MessageCircle, title: 'Have Questions?', desc: 'Chat with us for Quick Assistance' },
  ];

  return (
    <footer className="bg-[#1a1a1a] text-neutral-300">
      {/* Features strip */}
      <div className="border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-full border border-[#b8892d]/40 flex items-center justify-center text-[#b8892d] group-hover:bg-[#b8892d] group-hover:text-white transition-all">
                <f.icon size={22}/>
              </div>
              <div>
                <div className="text-white font-semibold text-sm tracking-wide">{f.title}</div>
                <div className="text-xs text-neutral-400">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="flex items-center gap-2 mb-4">
            {settings?.logo_image ? (
              <img 
                src={settings.logo_image} 
                alt={settings.siteName || "Watch Trader"} 
                style={{ maxWidth: settings.logo_width ? `${settings.logo_width}px` : '200px' }} 
                className="object-contain" 
              />
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-[#b8892d] flex items-center justify-center text-white font-serif text-xl">W</div>
                <div>
                  <div className="font-serif text-xl text-white">Watch Trader</div>
                  <div className="text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Premium Luxury Watches</div>
                </div>
              </>
            )}
          </Link>
          <p className="text-sm text-neutral-400 leading-relaxed">India's most trusted destination for premium luxury watches. Over 10+ years of experience in delivering luxury timepieces at affordable prices.</p>
          <div className="flex gap-3 mt-5">
            {[
              { name: 'Facebook', Icon: Facebook },
              { name: 'Instagram', Icon: Instagram },
              { name: 'Twitter', Icon: Twitter },
              { name: 'Youtube', Icon: Youtube },
            ].map(({ name, Icon }) => (
              <a key={name} href="#" className="w-9 h-9 border border-neutral-700 flex items-center justify-center rounded-full hover:bg-[#b8892d] hover:border-[#b8892d] transition-colors">
                <Icon size={16}/>
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 tracking-wide uppercase text-sm">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop?gender=men" className="hover:text-[#b8892d] transition-colors">Watches For Him</Link></li>
            <li><Link to="/shop?gender=women" className="hover:text-[#b8892d] transition-colors">Watches For Her</Link></li>
            <li><Link to="/shop" className="hover:text-[#b8892d] transition-colors">New Arrivals</Link></li>
            <li><Link to="/shop" className="hover:text-[#b8892d] transition-colors">Best Sellers</Link></li>
            <li><Link to="/shop" className="hover:text-[#b8892d] transition-colors">All Brands</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 tracking-wide uppercase text-sm">Information</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[#b8892d] transition-colors">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-[#b8892d] transition-colors">Contact Us</Link></li>
            <li><a href="#faq" className="hover:text-[#b8892d] transition-colors">FAQs</a></li>
            <li><a href="#" className="hover:text-[#b8892d] transition-colors">Shipping Policy</a></li>
            <li><a href="#" className="hover:text-[#b8892d] transition-colors">Return &amp; Refund</a></li>
            <li><a href="#" className="hover:text-[#b8892d] transition-colors">Privacy Policy</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 tracking-wide uppercase text-sm">Get in Touch</h4>
          <ul className="space-y-3 text-sm text-neutral-400">
            <li>{settings?.contactAddress || 'New Delhi, India'}</li>
            <li>{settings?.contactPhone || '+91 98765 43210'}</li>
            <li>{settings?.contactEmail || 'support@watchtrader.in'}</li>
            <li className="pt-2">Mon - Sat: 10:00 AM - 8:00 PM</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Watch Trader. All rights reserved.</div>
          <div>Payment: Paytm · Google Pay · PhonePe · Bank Transfer · Cards</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
